import ShowRow from './ShowRow';
import axios from 'axios';

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from 'react-dom';

// Redux.
import { connect } from 'react-redux';
import {
    appendRelatedPreviews,
    setNextPreviewsInfo,
    setTrack,
} from '../redux/actions/dataActions'
import {
    preloadTopicPreferences,
    setPodcastSubscribeStatus,
    setSpeakerSubscribeStatus,
    setTopicPreferenceStatus,
} from '../redux/actions/userActions';

// Components.
import CloseFooter from './CloseFooter';
import EntityInfoDrawer from './EntityInfoDrawer';

import PreviewActions from './PreviewActions';
import PreviewMoreOrLessDrawer from './PreviewMoreOrLessDrawer';
import PreviewOptionRow from './PreviewOptionRow';
import PreviewBookmarkButton from './PreviewBookmarkButton';
import PreviewReportIssueDrawer from './PreviewReportIssueDrawer';
import PreviewSigninDrawer from './PreviewSigninDrawer';
import StatusToast from './StatusToast';
import { LABEL_DOMAIN } from '../constants/labels';
import { shareContent, SHARE_FAILURE_MESSAGE } from '../utils/shareContent';
import PreviewFullEpisodeButton from './PreviewFullEpisodeButton';
import { firstEpisodeDuration, previewContinuationSeconds } from '../utils/episodePlaybackLabel';
import SyncTranscriptAudio from './SyncTranscriptAudio';
import Transcript from './Transcript';


import { cancelPendingAudioSeek, loadTrack } from './audioHelper';
import { formatPreviewInfoPublishedDate, getDateString } from './timeHelper';
import useIsInViewport from "./useIsInViewport";
import useWindowDimensions from "./windowHelper";


import { PLAYBACK_RATE } from '../constants/local_storage_constants';

import { PREVIEW_PREFERENCE_COPY_TITLE } from '../constants/labels';

import {
    ChevronLeftRounded,
    ChevronRightRounded,
    CloseRounded,
    MoreHorizRounded,
    PlayArrowRounded,
    HomeOutlined,
    IosShareRounded,
    ReportProblemOutlined,
    SkipPrevious,
    TuneOutlined,
} from '@mui/icons-material';
import { Drawer, SwipeableDrawer } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';

// Firebase.
import MoreWithPlaybackSpeedButton from './MoreWithPlaybackSpeedButton';
import ProgressBarThin from './ProgressBarThin';
import ContinueListeningButton from './ContinueListeningButton';
import FullWidthButton from './FullWidthButton';
import PreviewInfoSummary from './PreviewInfoSummary';
import SpeakerRow from './SpeakerRow';
import { isUnidentifiedSpeaker } from './speakerHelper';
import PreviewImage from './PreviewImage';
import usePreviewPlaybackControl from './usePreviewPlaybackControl';
import usePreviewPullDismiss from './usePreviewPullDismiss';
import { readOnboardingSession, recordOnboardingListening, updateOnboardingSession } from '../utils/onboardingSession';
import { trackOnboardingError, trackOnboardingEvent } from '../utils/onboardingFunnel';
import { isPlayableChatPreview, playableChatPreviews } from '../utils/chatPreviewQueue';
import { getPreviewTopic, previewTopicKey } from '../utils/previewTopic';
import { getPreviewQueuePresentation } from '../utils/previewQueuePresentation';
import { savePreviewViewerSession } from '../utils/previewViewerSessions';
import { createPreviewListening, finishActivePreviewListening } from '../utils/previewListening';
import PreviewQueueDrawer from './PreviewQueueDrawer';
import PreviewTopicRow from './PreviewTopicRow';
import PrimaryActionButton from './PrimaryActionButton';
import './PreviewCardOnboarding.css';
import './PreviewDiscovery.css';


const PREVIEW_TRANSCRIPT_LOADING_LINE_WIDTHS = [
    ['94%', '78%', '58%'],
    ['88%', '96%', '42%'],
    ['98%', '72%'],
    ['90%', '64%', '48%'],
];

const NOOP = () => {};
const RESOLVED_NOOP = () => Promise.resolve();


const PreviewCard = ({
    preview,
    previewIndexInViewport,
    finishedPreview,
    finishedPreviewIds,
    playFullEpisode,
    index,
    nextPreview,
    onOpenQueue,
    onCloseQueue,
    onSelectQueuedPreview,
    queuedPreviewSelection,
    isIntentionalPreviewExit,
    closeButtonHost,
    controlsHost,
    isChromeOwner = false,
    shouldLoadTrack,
    playbackEnabled = true,
    autoAdvanceEnabled = true,
    showsPlaybackHint = true,
    onPlaybackHintExpired,
    showAudioPlayerDrawer,
    viewportHeight,
    onClose,
    onboardingSession = null,
    onboardingExit = false,
    resumePreviewPosition,
    onSignupRequired = null,
    onSampleEnded = null,

    // Redux state.
    authenticated,
    saveDrawerOpen = false,
    savePresentation = null,
    nextPreviewsInfo,
    track,
    speakerSubscribeIds,
    speakerSubscribeStatusById,
    podcastSubscribeIds,
    podcastSubscribeStatusById,
    topicPreferenceStatusById = {},
    topicPreferencesLoaded = false,
    topicPreferencesLoading = false,
    topicPreferencesError = null,

    // Redux actions.
    setNextPreviewsInfo,
    appendRelatedPreviews,
    setTrack,
    setSpeakerSubscribeStatus,
    setPodcastSubscribeStatus,
    preloadTopicPreferences = NOOP,
    setTopicPreferenceStatus = RESOLVED_NOOP,
}) => {
    const previewCardViewport = useRef();
    const currentViewport = useRef();
    const transcriptContainerRef = useRef();
    const controlsSurfaceRef = useRef(null);

    const audio = document.getElementById('audio');

    const setAudioTime = useRef();
    const onPause = useRef();
    const onPlay = useRef();
    const onActualPlaying = useRef();
    const restoreSamplePosition = useRef();
    const restorePreviewPosition = useRef();
    const onEndedListener = useRef();
    const completionBehavior = useRef(null);
    const sampleEndedCallback = useRef(onSampleEnded);
    sampleEndedCallback.current = onSampleEnded;
    const initialPlayButton = useRef(null);
    const playbackButton = useRef(null);
    const closeButton = useRef(null);
    const queueTriggerRef = useRef(null);
    const queueReturnFocus = useRef(null);
    const handoffPlaybackFocus = useRef(false);
    const handoffCloseFocus = useRef(false);
    const [sampleStarted, setSampleStarted] = useState(() =>
        Boolean(onboardingSession?.sampleStarted) || (onboardingSession?.position || 0) > 0);
    const sampleStartedRef = useRef(sampleStarted);
    const showInitialPlayAction = Boolean(onSignupRequired && !sampleStarted);
    const [showPlayIntro, setShowPlayIntro] = useState(() => Boolean(onSignupRequired
        && !sampleStarted && !onboardingSession?.playIntroShown));

    useEffect(() => {
        if (showInitialPlayAction && showPlayIntro) {
            updateOnboardingSession({ playIntroShown: true }, { newId: onboardingSession?.newId, previewId: preview.id });
        }
    }, [showInitialPlayAction, showPlayIntro, onboardingSession?.newId, preview.id]);

    useEffect(() => {
        if (sampleStarted && handoffPlaybackFocus.current) {
            handoffPlaybackFocus.current = false;
            playbackButton.current?.focus({ preventScroll: true });
        }
        if (sampleStarted && handoffCloseFocus.current) {
            handoffCloseFocus.current = false;
            closeButton.current?.focus({ preventScroll: true });
        }
    }, [sampleStarted]);

    const isVisible = useIsInViewport(currentViewport);
    // The one free sample has no horizontal neighbor. Transcript autoscroll
    // can move its tiny visibility sentinel out of view while audio continues;
    // keep the actual sample active so its controls and ended listener persist.
    const isCurrentViewport = playbackEnabled && (isVisible || Boolean(onboardingSession));
    const [localPreview, setLocalPreview] = useState(null);
    const queuePresentation = getPreviewQueuePresentation({
        ...nextPreviewsInfo,
        previewsInfo: nextPreviewsInfo?.previewsInfo || [preview],
    }, preview.id);
    const activeTopicKey = previewTopicKey(queuePresentation.activeTopic);
    const topicGroups = new Map();
    (Array.isArray(localPreview?.relatedTopicPreviews) ? localPreview.relatedTopicPreviews : []).forEach(group => {
        const key = previewTopicKey(group?.id);
        if (!key) return;
        const existing = topicGroups.get(key);
        topicGroups.set(key, { id: existing?.id || group.id.trim().replace(/\s+/g, ' '),
            previews: playableChatPreviews([...(existing?.previews || []), ...(Array.isArray(group.previews) ? group.previews : [])]),
        });
    });
    const visibleRelatedTopics = [];
    const visibleTopicKeys = new Set();
    [queuePresentation.activeTopic, getPreviewTopic(preview, { uppercase: false }), ...[...topicGroups.values()].map(group => group.id)]
        .forEach(name => {
            const key = previewTopicKey(name);
            if (!key || visibleTopicKeys.has(key)) return;
            const topicPreviews = topicGroups.get(key) || { id: name.trim().replace(/\s+/g, ' '), previews: [] };
            if (key !== activeTopicKey && !topicPreviews.previews.some(candidate => candidate.id !== preview.id)) return;
            visibleTopicKeys.add(key);
            visibleRelatedTopics.push(topicPreviews);
        });
    const [missingPreview, setMissingPreview] = useState(false);
    const [scrollToCurrentMessage, setScrollToCurrentMessage] = useState(true);
    const previousShowAudioPlayerDrawer = useRef(showAudioPlayerDrawer);
    const restoringPreview = Number.isFinite(resumePreviewPosition) && resumePreviewPosition >= 0;
    const restoredPreviewPosition = useRef(false);
    const [replayAvailable, setReplayAvailable] = useState(false);
    const [replayRequest, setReplayRequest] = useState(0);
    const suppressPreviewAutoplay = useRef(Boolean(onboardingSession) || restoringPreview);
    const moreOrLessPlaybackState = useRef(null);
    const fullEpisodeDrawerPlaybackState = useRef(null);
    const pendingActionFromSummary = useRef(null);
    const signinPlaybackState = useRef(null);
    const reportPlaybackState = useRef(null);
    const actionAfterSignin = useRef(null);
    const continuingFullEpisode = useRef(false);
    const queuePlaybackState = useRef(null);
    const [showQueue, setShowQueue] = useState(false);

    const [playing, setPlaying] = useState(false);
    const audioPreparationActive = isCurrentViewport && !missingPreview && !showQueue &&
        !showAudioPlayerDrawer;
    const [curTime, setCurTime] = useState(0);
    const [duration, setDuration] = useState(null);

    const [openPersonInfo, setOpenPersonInfo] = useState(false);
    const [showReport, setShowReport] = useState(false);
    const [signinAction, setSigninAction] = useState(null);
    const [copied, setCopied] = useState(false);
    const [shareError, setShareError] = useState('');
    const [showPreviewActions, setShowPreviewActions] = useState(false);
    const [showMoreOrLess, setShowMoreOrLess] = useState(false);
    const [showFullEpisodeContinueOrStart, setShowFullEpisodeContinueOrStart] = useState(false);
    const viewerInteractionAvailable = isCurrentViewport && !missingPreview && !showAudioPlayerDrawer && !saveDrawerOpen
        && !showPreviewActions && !showQueue && !showMoreOrLess && !showFullEpisodeContinueOrStart
        && !showReport && !signinAction && !openPersonInfo;
    const autoAdvanceAvailable = autoAdvanceEnabled && viewerInteractionAvailable;
    const nextQueuedPreview = nextPreviewsInfo?.previewsInfo?.[index + 1];
    const canAdvanceToNextPreview = autoAdvanceAvailable && isPlayableChatPreview(nextQueuedPreview);
    const playbackControl = usePreviewPlaybackControl({
        audio,
        source: preview.mp3URL,
        active: isCurrentViewport && !missingPreview && !showAudioPlayerDrawer,
        viewerAvailable: autoAdvanceAvailable && !showReport,
        willAutomaticallyAdvance: canAdvanceToNextPreview && !onboardingSession && !replayAvailable,
        finished: finishedPreviewIds.current.has(preview.id) || replayAvailable,
        showsPlaybackHint,
        onPlaybackHintExpired,
        autoplayPending: !suppressPreviewAutoplay.current,
        keepVisible: !scrollToCurrentMessage,
    });
    const requestPreviewPlayback = playbackControl.requestPlayback;
    const loadPreviewAudio = useCallback(() => {
        requestPreviewPlayback({
            reveal: false,
            start: () => audio.src === preview.mp3URL
                ? audio.play()
                : loadTrack(preview.mp3URL, 0, true, true),
        }).catch(() => {});
    }, [audio, requestPreviewPlayback, preview.mp3URL]);

    const previewListening = useRef(null);
    const previewDuration = useRef(preview.totalTimeSeconds);
    previewDuration.current = preview.totalTimeSeconds;
    const intentionalPreviewExit = useRef(isIntentionalPreviewExit);
    intentionalPreviewExit.current = isIntentionalPreviewExit;

    useEffect(() => {
        if (!audio || !authenticated || !isCurrentViewport) return undefined;
        const listening = createPreviewListening({
            audio,
            preview: { id: preview.id, mp3URL: preview.mp3URL },
            getDuration: () => previewDuration.current,
        });
        previewListening.current = listening;
        const listeners = {
            playing: listening.start,
            timeupdate: listening.checkpoint,
            pause: listening.flush,
        };
        Object.entries(listeners).forEach(([event, listener]) => audio.addEventListener(event, listener));
        if (audio.src === preview.mp3URL && !audio.paused && !audio.ended) listening.start();
        return () => {
            listening.finish(intentionalPreviewExit.current?.(index) ? 'skipped' : 'interrupted');
            Object.entries(listeners).forEach(([event, listener]) => audio.removeEventListener(event, listener));
            if (previewListening.current === listening) previewListening.current = null;
        };
    }, [audio, authenticated, isCurrentViewport, preview.id, preview.mp3URL, index]);

    const { height } = useWindowDimensions();

    useEffect(() => {
        if (!queuedPreviewSelection) return;
        previewListening.current?.reset();
        if (restorePreviewPosition.current) {
            audio.removeEventListener('loadedmetadata', restorePreviewPosition.current);
            restorePreviewPosition.current = null;
        }
        suppressPreviewAutoplay.current = false;
        restoredPreviewPosition.current = true;
        setCurTime(0);
        setReplayAvailable(false);
        setReplayRequest(request => request + 1);
        setScrollToCurrentMessage(true);
    }, [queuedPreviewSelection, audio]);

    useEffect(() => {
        const didReturnFromFullEpisode =
            previousShowAudioPlayerDrawer.current && !showAudioPlayerDrawer;
        previousShowAudioPlayerDrawer.current = showAudioPlayerDrawer;

        if (!isCurrentViewport) {
            suppressPreviewAutoplay.current = Boolean(queuePlaybackState.current || onboardingSession)
                || (restoringPreview && !restoredPreviewPosition.current);
            return;
        }
        if (didReturnFromFullEpisode) {
            continuingFullEpisode.current = false;
            suppressPreviewAutoplay.current = true;
            audio.pause();
        }
    }, [audio, showAudioPlayerDrawer, isCurrentViewport, onboardingSession, restoringPreview]);

    const savedDrawerPlayback = useRef(null);
    useEffect(() => {
        if (saveDrawerOpen && !savedDrawerPlayback.current && isCurrentViewport) {
            savedDrawerPlayback.current = { previewId: preview.id,
                shouldResume: Boolean(savePresentation?.shouldResume), wasSuppressed: suppressPreviewAutoplay.current };
            suppressPreviewAutoplay.current = true;
            if (audio.src === preview.mp3URL) audio.pause();
        } else if (!saveDrawerOpen && savedDrawerPlayback.current) {
            const captured = savedDrawerPlayback.current;
            savedDrawerPlayback.current = null;
            if (captured.previewId === preview.id && isCurrentViewport) {
                const shouldResume = captured.shouldResume && !showAudioPlayerDrawer
                    && audio.src === preview.mp3URL && !finishedPreviewIds.current.has(preview.id);
                suppressPreviewAutoplay.current = captured.wasSuppressed || !shouldResume;
                if (shouldResume && audio.paused) requestPreviewPlayback({ reveal: false }).catch(() => {});
            }
        }
    }, [audio, finishedPreviewIds, isCurrentViewport, preview.id, preview.mp3URL,
        saveDrawerOpen, savePresentation, showAudioPlayerDrawer, requestPreviewPlayback]);

    useEffect(() => {
        if (onSignupRequired) return;
        if ((localPreview || (missingPreview && nextPreviewsInfo?.relatedTopicId)) && nextPreviewsInfo?.relatedPreviewId === preview.id && !nextPreviewsInfo.relatedPreviewsAppended) {
            appendRelatedPreviews(authenticated, nextPreviewsInfo.queueId, preview.id, localPreview?.relatedTopicPreviews || []);
        }
    }, [localPreview, missingPreview, nextPreviewsInfo, preview.id, authenticated, appendRelatedPreviews, onSignupRequired]);

    const [reloadTranscript, setReloadTranscript] = useState(false);
    const pullDismissEnabled = viewerInteractionAvailable && Boolean(localPreview) && !reloadTranscript && Boolean(onClose);
    const pullDismiss = usePreviewPullDismiss(transcriptContainerRef, { enabled: pullDismissEnabled, onDismiss: onClose });

    const restorePausedPreviewAudio = useCallback(position => {
        cancelPendingAudioSeek(audio);
        audio.pause();
        if (restorePreviewPosition.current) {
            audio.removeEventListener('loadedmetadata', restorePreviewPosition.current);
        }
        if (audio.src !== preview.mp3URL) audio.src = preview.mp3URL;
        else if (audio.error) audio.load();
        const restorePosition = () => {
            if (audio.src === preview.mp3URL) audio.currentTime = position;
        };
        restorePreviewPosition.current = restorePosition;
        restorePosition();
        audio.addEventListener('loadedmetadata', restorePosition, { once: true });
        setCurTime(position);
    }, [audio, preview.mp3URL]);

    useEffect(() => {
        let transcriptReloadTimer;
        if (!isCurrentViewport) {
            // Reload transcript so when user goes back, the transcript starts at the top.
            setReloadTranscript(true);
            transcriptReloadTimer = setTimeout(() => {
                setReloadTranscript(false);
            }, 100);

        } else {
            setReloadTranscript(false);
            previewIndexInViewport(index);
            if (missingPreview) {
                suppressPreviewAutoplay.current = true;
                if (audio.src === preview.mp3URL) {
                    cancelPendingAudioSeek(audio);
                    audio.pause();
                }
            } else if (onboardingSession) {
                audio.pause();
                if (audio.src !== preview.mp3URL) audio.src = preview.mp3URL;
                audio.currentTime = onboardingSession.position;
                restoreSamplePosition.current = () => {
                    const session = readOnboardingSession();
                    if (session && audio.src === preview.mp3URL && session.newId === onboardingSession.newId
                        && session.sample.id === preview.id) audio.currentTime = session.position;
                };
                audio.addEventListener('loadedmetadata', restoreSamplePosition.current, { once: true });
                setCurTime(onboardingSession.position);
            } else if (restoringPreview && !restoredPreviewPosition.current) {
                restoredPreviewPosition.current = true;
                suppressPreviewAutoplay.current = true;
                restorePausedPreviewAudio(resumePreviewPosition);
                const clipDuration = Number(previewDuration.current);
                setReplayAvailable(finishedPreviewIds.current.has(preview.id) ||
                    (Number.isFinite(clipDuration) && clipDuration > 0 && resumePreviewPosition >= clipDuration - 0.05));
            } else if (finishedPreviewIds.current.has(preview.id)) {
                setReplayAvailable(true);
            } else if (!suppressPreviewAutoplay.current) {
                playPreview();
            }

            const pathName = `/preview/${preview.fullTrackId}/${preview.messageIndex}`;
            window.history.replaceState(window.history.state, '', pathName);

            setAudioTime.current = () => {
                if (isCurrentViewport && audio.src === preview.mp3URL) {
                    setCurTime(audio.currentTime);
                    if (onboardingSession) {
                        updateOnboardingSession({ position: audio.currentTime }, { newId: onboardingSession.newId, previewId: preview.id });
                    }

                    if (!playing && !audio.paused) {
                        setPlaying(true);
                    }
                    if (!duration) {
                        setDuration(audio.duration);
                    }
                }
            }

            onPause.current = (event) => {
                setPlaying(false);
            }

            onPlay.current = () => {
                // Do not restart unavailable clips or clips covered by a drawer.
                // A replacement source still owns its own playback.
                if (audio.src !== preview.mp3URL) return;
                const playbackBlocked = missingPreview || [queuePlaybackState, moreOrLessPlaybackState,
                    fullEpisodeDrawerPlaybackState, pendingActionFromSummary, signinPlaybackState,
                    reportPlaybackState, savedDrawerPlayback]
                    .some(state => state.current?.previewId === preview.id);
                if (playbackBlocked) {
                    audio.pause();
                    return;
                }
                setPlaying(true);
            }

            onActualPlaying.current = () => {
                if (audio.src === preview.mp3URL) setPlaying(true);
                if (onboardingSession && audio.src === preview.mp3URL) {
                    trackOnboardingEvent('onboarding_play_start');
                }
                if (onboardingSession && audio.src === preview.mp3URL && !sampleStartedRef.current) {
                    sampleStartedRef.current = true;
                    handoffPlaybackFocus.current = document.activeElement === initialPlayButton.current;
                    handoffCloseFocus.current = document.activeElement === closeButton.current;
                    updateOnboardingSession({ sampleStarted: true }, { newId: onboardingSession.newId, previewId: preview.id });
                    setSampleStarted(true);
                }
            };

            // DOM listeners: update React state on DOM events
            audio.addEventListener('timeupdate', setAudioTime.current);
            audio.addEventListener('pause', onPause.current);
            audio.addEventListener('play', onPlay.current);
            audio.addEventListener('playing', onActualPlaying.current);
            const onEnded = () => {
                if (audio.src === preview.mp3URL) {
                    // Capture completion before callbacks can advance or
                    // replace the shared audio element.
                    previewListening.current?.finish('completed');
                    setPlaying(false);
                    const completion = completionBehavior.current;
                    const drawersClosing = [queuePlaybackState, moreOrLessPlaybackState,
                        fullEpisodeDrawerPlaybackState, pendingActionFromSummary, signinPlaybackState, reportPlaybackState]
                        .some(state => state.current?.previewId === preview.id);
                    const shouldAdvance = completion.canAdvanceToNextPreview
                        && !drawersClosing && !continuingFullEpisode.current;
                    if (!onboardingSession) {
                        // A completion held by a drawer stays replayable after it closes.
                        setReplayAvailable(!shouldAdvance);
                    }
                    sampleEndedCallback.current?.(preview.id);
                    if (!completion.onSignupRequired && !completion.onboardingSession) {
                        completion.finishedPreview(shouldAdvance);
                    }
                }
            };
            audio.addEventListener('ended', onEnded);
            onEndedListener.current = onEnded;
        }
        return () => {
            clearTimeout(transcriptReloadTimer);
            audio.removeEventListener('timeupdate', setAudioTime.current);
            audio.removeEventListener('pause', onPause.current);
            audio.removeEventListener('play', onPlay.current);
            audio.removeEventListener('playing', onActualPlaying.current);
            if (restoreSamplePosition.current) audio.removeEventListener('loadedmetadata', restoreSamplePosition.current);
            if (restorePreviewPosition.current) {
                audio.removeEventListener('loadedmetadata', restorePreviewPosition.current);
                restorePreviewPosition.current = null;
            }
            if (onEndedListener.current) audio.removeEventListener('ended', onEndedListener.current);
        };
    }, [isCurrentViewport, missingPreview, restoringPreview, resumePreviewPosition, restorePausedPreviewAudio]);

    useEffect(() => {
        if (!onboardingSession?.newId || !isCurrentViewport) return undefined;
        const newId = onboardingSession.newId;
        let active = false;
        let seeking = false;
        let lastPosition = audio.currentTime;
        let lastTimestamp = performance.now();
        const matchesSample = () => audio.src === preview.mp3URL;
        const resetInterval = () => {
            lastPosition = audio.currentTime;
            lastTimestamp = performance.now();
        };
        const accountListening = () => {
            const timestamp = performance.now();
            const delta = audio.currentTime - lastPosition;
            const elapsed = Math.max(0, timestamp - lastTimestamp) / 1000;
            // Media time advancing at the playback rate is listening. A jump
            // beyond elapsed playback time is a seek, even if its DOM seeking
            // event has not arrived yet.
            if (active && !seeking && !audio.seeking && matchesSample() && delta > 0
                && delta <= elapsed * (audio.playbackRate || 1) + 0.5) {
                recordOnboardingListening({
                    newId, previewId: preview.id, listenedSeconds: delta,
                    duration: audio.duration,
                });
            }
            lastPosition = audio.currentTime;
            lastTimestamp = timestamp;
        };
        const onPlaying = () => {
            accountListening();
            active = matchesSample() && !audio.paused;
            resetInterval();
        };
        const onTimeUpdate = () => {
            if (audio.paused && !audio.ended) {
                active = false;
                resetInterval();
            } else accountListening();
        };
        const onStopped = () => {
            accountListening();
            active = false;
        };
        const onSeeking = () => {
            seeking = true;
            resetInterval();
        };
        const onSeeked = () => {
            seeking = false;
            active = matchesSample() && !audio.paused && !audio.ended;
            resetInterval();
        };
        const listeners = {
            playing: onPlaying, timeupdate: onTimeUpdate, pause: onStopped,
            ended: onStopped, waiting: onStopped,
            seeking: onSeeking, seeked: onSeeked,
        };
        Object.entries(listeners).forEach(([event, listener]) => audio.addEventListener(event, listener));
        return () => {
            accountListening();
            Object.entries(listeners).forEach(([event, listener]) => audio.removeEventListener(event, listener));
        };
    }, [audio, isCurrentViewport, onboardingSession?.newId, preview.id, preview.mp3URL]);


    const playPreview = () => {
        previewListening.current?.reset();
        setCurTime(0);
        loadPreviewAudio();
    }

    const scrollToPreviewCard = () => {
        const previewCardElem = document.getElementById(preview.id);
        if (previewCardElem) {
            previewCardElem.scrollIntoView({ behavior: "smooth", block: 'nearest', inline: "start" });
        }
    }

    useEffect(() => {
        let cancelled = false;
        const pathName = `/preview/${preview.fullTrackId}/${preview.messageIndex}`;
        if (shouldLoadTrack && !localPreview) {
            if (track && track.pathname === pathName) {
                setLocalPreview(track);
            } else {
                axios
                    .get(pathName)
                    .then((res) => {
                        if (!cancelled) setLocalPreview(res.data);
                    })
                    .catch((err) => {
                        if (!cancelled) {
                            console.log(err);
                            setMissingPreview(true);
                        }
                    });
            }
        }
        return () => { cancelled = true; };
    }, [shouldLoadTrack]);

    useEffect(() => {
        if (isCurrentViewport && localPreview && !showAudioPlayerDrawer) {
            setTrack(localPreview);
        }
    }, [isCurrentViewport, localPreview, showAudioPlayerDrawer])

    const checkAudioSource = () => {
        if (audio.src !== preview.mp3URL) {
            if (!finishedPreviewIds.current.has(preview.id)) {
                playPreview(preview);
            }
        }
        if (localPreview) {
            setTrack(localPreview);
        }
    }

    const selectedSpeakerId = useRef('');

    const togglePersonInfo = (open) => (event) => {
        if (event && event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
            return;
        }
        setOpenPersonInfo(open);
    }

    const openSpeakerInfo = (speakerId) => () => {
        if (onSignupRequired) {
            onSignupRequired('speaker');
            return;
        }
        selectedSpeakerId.current = speakerId;
        setTimeout(() => {
            setOpenPersonInfo(true);
        }, 100);
    }

    const navigate = useNavigate();
    const location = useLocation();
    const navToSource = (sourceId) => () => {
        if (onSignupRequired) {
            onSignupRequired('podcast');
            return;
        }
        navigate(`/podcast/${sourceId}`)
    }

    useEffect(() => {
        if (isCurrentViewport && !missingPreview && !showAudioPlayerDrawer) {
            if (suppressPreviewAutoplay.current) {
                audio.pause();
            } else if (openPersonInfo) {
                audio.pause();
            } else {
                checkAudioSource();
            }
        }
    }, [openPersonInfo, isCurrentViewport, missingPreview, showAudioPlayerDrawer])

    const safeAreaBottom = parseInt(getComputedStyle(document.documentElement).getPropertyValue("--sab")) || 0;

    completionBehavior.current = { onSignupRequired, onboardingSession, finishedPreview, canAdvanceToNextPreview };
    const hasFullEpisode = Boolean(localPreview?.trackMp3URL && localPreview.id === preview.id);
    const countdownEnabled = Boolean(nextPreviewsInfo?.playbackSource !== 'saved' && hasFullEpisode && canAdvanceToNextPreview && !onSignupRequired && !onboardingSession
        && !finishedPreviewIds.current.has(preview.id)
        && !queuePlaybackState.current && !moreOrLessPlaybackState.current
        && !fullEpisodeDrawerPlaybackState.current && !pendingActionFromSummary.current
        && !signinPlaybackState.current && !reportPlaybackState.current
        && !continuingFullEpisode.current);

    const pausePreviewForDrawer = (drawerPlaybackState, shouldResumeOverride) => {
        const shouldResume = shouldResumeOverride ?? (
            isCurrentViewport &&
            !audio.paused &&
            !audio.ended &&
            audio.src === preview.mp3URL);

        drawerPlaybackState.current = {
            previewId: preview.id,
            shouldResume,
        };

        if (shouldResume && !audio.paused && audio.src === preview.mp3URL) {
            audio.pause();
        }
    }

    const resumePreviewAfterDrawer = (drawerPlaybackState) => {
        const playbackState = drawerPlaybackState.current;
        drawerPlaybackState.current = null;

        if (
            playbackState?.shouldResume &&
            playbackState.previewId === preview.id &&
            isCurrentViewport &&
            !showAudioPlayerDrawer &&
            audio.paused &&
            audio.src === preview.mp3URL &&
            !finishedPreviewIds.current.has(preview.id)
        ) {
            requestPreviewPlayback({ reveal: false }).catch(function () { });
        }
    }

    const openMoreOrLess = (shouldResume) => {
        if (onSignupRequired) {
            onSignupRequired('preferences');
            return;
        }
        preloadTopicPreferences();
        pausePreviewForDrawer(moreOrLessPlaybackState, shouldResume);
        setShowMoreOrLess(true);
    }

    const closeMoreOrLess = () => {
        setShowMoreOrLess(false);
    }

    const moreOrLessExited = () => {
        resumePreviewAfterDrawer(moreOrLessPlaybackState);
    }

    const openReport = (shouldResume) => {
        pausePreviewForDrawer(reportPlaybackState, shouldResume);
        setShowReport(true);
    };

    const reportExited = () => {
        resumePreviewAfterDrawer(reportPlaybackState);
    };

    const closeSignin = () => {
        actionAfterSignin.current = 'summary';
        setSigninAction(null);
    };

    const completeActionSignin = () => {
        actionAfterSignin.current = signinAction;
        setSigninAction(null);
    };

    const signinExited = () => {
        const pending = signinPlaybackState.current;
        const action = actionAfterSignin.current;
        signinPlaybackState.current = null;
        actionAfterSignin.current = null;
        if (!pending || pending.previewId !== preview.id || !isCurrentViewport || showAudioPlayerDrawer
            || audio.src !== pending.audioSrc) return;

        if (action === 'preferences') openMoreOrLess(pending.shouldResume);
        else if (action === 'report') openReport(pending.shouldResume);
        else {
            fullEpisodeDrawerPlaybackState.current = pending;
            setShowFullEpisodeContinueOrStart(true);
        }
    };

    const togglePreviewActions = (open) => (event) => {
        if (event && event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
            return;
        }
        setShowPreviewActions(open);
    };
    const onSpeedClick = (val) => () => {
        audio.playbackRate = val;
        localStorage.setItem(PLAYBACK_RATE, val);
        togglePreviewActions(false)();
    }

    const restoredOnboardingAction = useRef(false);

    useEffect(() => {
        if (authenticated && onboardingSession?.action === 'full_episode'
            && localPreview?.trackMp3URL && !restoredOnboardingAction.current) {
            restoredOnboardingAction.current = true;
            setShowFullEpisodeContinueOrStart(true);
            updateOnboardingSession({ action: null, reason: null }, { newId: onboardingSession.newId, previewId: preview.id });
        }
    }, [authenticated, onboardingSession?.action, onboardingSession?.newId, localPreview?.trackMp3URL, preview.id]);

    const fullEpisodeClick = () => {
        if (onSignupRequired) {
            onSignupRequired('full_episode');
            return;
        }
        pausePreviewForDrawer(fullEpisodeDrawerPlaybackState);
        setShowFullEpisodeContinueOrStart(true);
    }

    const startFromBeginning = () => {
        continuingFullEpisode.current = true;
        fullEpisodeDrawerPlaybackState.current = null;
        setShowFullEpisodeContinueOrStart(false);
        playFullEpisode(localPreview.trackMp3URL, 0, localPreview);
    }

    const continueListening = () => {
        if (onSignupRequired) {
            onSignupRequired('full_episode');
            return;
        }
        if (!hasFullEpisode || !isCurrentViewport || showAudioPlayerDrawer
            || audio.src !== preview.mp3URL || continuingFullEpisode.current) return;
        const clipDuration = Number.isFinite(audio.duration) && audio.duration > 0
            ? audio.duration : Number(preview.totalTimeSeconds);
        const start = previewContinuationSeconds(localPreview.previewStartTime, audio.currentTime, clipDuration);
        if (start === null) return;
        continuingFullEpisode.current = true;
        fullEpisodeDrawerPlaybackState.current = null;
        pendingActionFromSummary.current = null;
        setShowFullEpisodeContinueOrStart(false);
        playFullEpisode(localPreview.trackMp3URL, start, localPreview);
    }

    const closeFullEpisodeDrawer = () => {
        setShowFullEpisodeContinueOrStart(false);
    }

    const fullEpisodeDrawerExited = () => {
        const pending = pendingActionFromSummary.current;
        if (pending) {
            // Transfer playback intent after the details sheet has closed, so
            // moving to sign-in or an action never briefly restarts the preview.
            if (pending.previewId === preview.id && isCurrentViewport && !showAudioPlayerDrawer
                && audio.src === pending.audioSrc) {
                if (!authenticated) {
                    signinPlaybackState.current = pending;
                    setSigninAction(pending.action);
                } else if (pending.action === 'report') {
                    openReport(pending.shouldResume);
                } else {
                    openMoreOrLess(pending.shouldResume);
                }
            }
            pendingActionFromSummary.current = null;
            return;
        }
        resumePreviewAfterDrawer(fullEpisodeDrawerPlaybackState);
    }

    const actionFromSummary = (action) => {
        pendingActionFromSummary.current = {
            action,
            previewId: preview.id,
            audioSrc: audio.src,
            shouldResume: Boolean(fullEpisodeDrawerPlaybackState.current?.shouldResume),
        };
        fullEpisodeDrawerPlaybackState.current = null;
        setShowFullEpisodeContinueOrStart(false);
    };

    const fullEpisodeInfo = localPreview || preview || track;
    const continuationSeconds = previewContinuationSeconds(
        fullEpisodeInfo.previewStartTime,
        audio.src === preview.mp3URL ? audio.currentTime : curTime,
        audio.src === preview.mp3URL && Number.isFinite(audio.duration) && audio.duration > 0
            ? audio.duration : preview.totalTimeSeconds,
    );
    const fullEpisodeDuration = firstEpisodeDuration(fullEpisodeInfo.fullTrackTotalTimeSecs, preview.fullTrackTotalTimeSecs);
    const fullEpisodeSummary = fullEpisodeInfo.summary ?? preview.summary;
    const fullEpisodeTopics = fullEpisodeInfo.topics?.length ? fullEpisodeInfo.topics : preview.topics;
    const knownEpisodeSpeakers = (Array.isArray(fullEpisodeInfo.speakers) ? fullEpisodeInfo.speakers : [])
        .filter(speaker => speaker?.id?.trim() && !isUnidentifiedSpeaker(speaker));
    const fullEpisodeSpeakers = knownEpisodeSpeakers.length
        ? [...new Map(knownEpisodeSpeakers.map(speaker => [speaker.id, speaker])).values()]
        : !isUnidentifiedSpeaker(preview.person) && preview.person?.id?.trim() ? [preview.person] : [];

    const replayPreview = () => {
        if (!isCurrentViewport || showAudioPlayerDrawer) return;
        previewListening.current?.reset();
        cancelPendingAudioSeek(audio);
        if (restorePreviewPosition.current) {
            audio.removeEventListener('loadedmetadata', restorePreviewPosition.current);
            restorePreviewPosition.current = null;
        }
        if (restoreSamplePosition.current) {
            audio.removeEventListener('loadedmetadata', restoreSamplePosition.current);
            restoreSamplePosition.current = null;
        }
        if (audio.src !== preview.mp3URL) audio.src = preview.mp3URL;
        else if (audio.error) audio.load();
        audio.currentTime = 0;
        setCurTime(0);
        finishedPreviewIds.current.delete(preview.id);
        setReplayAvailable(false);
        suppressPreviewAutoplay.current = false;
        setReplayRequest(request => request + 1);
        setScrollToCurrentMessage(true);
        // Keep playback in the tap handler so mobile browsers permit audio.
        requestPreviewPlayback().catch(() => {});
    };

    const onTranscriptManualScroll = useCallback(() => {
        suppressPreviewAutoplay.current = true;
    }, []);

    const onSyncClick = () => {
        if (!isCurrentViewport || showAudioPlayerDrawer || showQueue || audio.src !== preview.mp3URL) return;
        setScrollToCurrentMessage(true);
        const duration = Number.isFinite(audio.duration) && audio.duration > 0
            ? audio.duration : Number(preview.totalTimeSeconds);
        // Completed clips keep their existing Replay action.
        if (replayAvailable || finishedPreviewIds.current.has(preview.id) || audio.ended ||
            (duration > 0 && audio.currentTime >= duration - 0.05)) return;
        resumePreviewPlayback();
    };

    const resumePreviewPlayback = () => {
        if (!isCurrentViewport || showAudioPlayerDrawer) return;
        if (audio.src !== preview.mp3URL || audio.error) {
            restorePausedPreviewAudio(curTime);
        }
        suppressPreviewAutoplay.current = false;
        finishedPreviewIds.current.delete(preview.id);
        // Resume during the tap so browsers retain permission to play audio.
        if (audio.paused) requestPreviewPlayback().catch(() => {});
        else playbackControl.revealControl();
    }

    const goToPodcast = () => {
        fullEpisodeDrawerPlaybackState.current = null;
        audio.pause();
        navigate(`/podcast/${fullEpisodeInfo.source.id}`)
    }

    const onTopicPreviewsClick = (relatedTopicPreviews) => () => {
        if (onSignupRequired) {
            onSignupRequired('topic');
            return;
        }
        const filteredPreviews = playableChatPreviews(
            Array.isArray(relatedTopicPreviews.previews) ? relatedTopicPreviews.previews : [],
        ).filter(p => p.id !== preview.id);
        if (!filteredPreviews.length || !previewTopicKey(relatedTopicPreviews.id)) return;
        const first = filteredPreviews[0];
        savePreviewViewerSession(location, nextPreviewsInfo, preview.id, audio.currentTime);
        finishActivePreviewListening(audio, 'skipped');
        loadTrack(first.mp3URL, 0);
        setNextPreviewsInfo(authenticated, [first], {
            initialPreviewId: first.id,
            relatedPreviewId: first.id,
            relatedTopicId: relatedTopicPreviews.id.trim(),
            topicPreviewCandidates: filteredPreviews,
        });
        navigate('/previews', { state: { closeAllModal: true, title: relatedTopicPreviews.id, ...(onboardingExit ? { onboardingExit: true } : {}) } });
    }

    const openQueue = (event) => {
        if (!isCurrentViewport || showQueue || showAudioPlayerDrawer || onSignupRequired) return;
        queueReturnFocus.current = { trigger: event?.currentTarget || queueTriggerRef.current };
        queuePlaybackState.current = { previewId: preview.id, queueId: nextPreviewsInfo?.queueId };
        suppressPreviewAutoplay.current = true;
        audio.pause();
        onOpenQueue?.(preview.id);
        setShowQueue(true);
    };

    const closeQueue = () => {
        const saved = queuePlaybackState.current;
        queuePlaybackState.current = null;
        setShowQueue(false);
        onCloseQueue?.();
        if (saved?.previewId === preview.id && saved.queueId === nextPreviewsInfo?.queueId
            && isCurrentViewport && !showAudioPlayerDrawer && audio.src === preview.mp3URL) {
            suppressPreviewAutoplay.current = false;
            requestPreviewPlayback({ reveal: false }).catch(() => {});
        }
    };

    const queueExited = () => {
        if (showQueue) return;
        const saved = queueReturnFocus.current;
        queueReturnFocus.current = null;
        const target = saved?.previewId
            ? document.getElementById(saved.previewId)?.querySelector('.previewQueueChip')
            : saved?.trigger;
        if (target?.isConnected) target.focus({ preventScroll: true });
    };

    const selectQueuedPreview = (id) => {
        if (id === preview.id) {
            closeQueue();
            return;
        }
        if (!onSelectQueuedPreview) return;
        // Closing for a selection must not resume the old clip.
        queueReturnFocus.current = { previewId: id };
        queuePlaybackState.current = null;
        setShowQueue(false);
        onSelectQueuedPreview(id);
    };

    const [bottomMenuHeight, setBottomMenuHeight] = useState(87);
    const cardHeight = viewportHeight || Math.max(height - safeAreaBottom, 320);
    const usesSharedChrome = Boolean(closeButtonHost && controlsHost && !onSignupRequired);
    useLayoutEffect(() => {
        const surface = controlsSurfaceRef.current;
        if (!surface) return undefined;
        const measure = () => {
            const measured = Math.ceil(surface?.getBoundingClientRect().height || 0);
            if (measured > 0) setBottomMenuHeight(previous => previous === measured ? previous : measured);
        };
        measure();
        const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
        observer?.observe(surface);
        window.addEventListener('resize', measure);
        return () => {
            observer?.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, [usesSharedChrome, isChromeOwner, controlsHost, closeButtonHost, onSignupRequired, showInitialPlayAction, missingPreview]);
    const sliderHeight = Math.max(cardHeight - (missingPreview ? 0 : bottomMenuHeight), 220);
    const publishedDateText = formatPreviewInfoPublishedDate(preview.publishedDate);

    const togglePreviewPlayback = () => {
        if (!isCurrentViewport || showAudioPlayerDrawer) return;
        const isPreviewSource = audio.src === preview.mp3URL;
        if (isPreviewSource && !audio.paused && !audio.ended) {
            suppressPreviewAutoplay.current = true;
            audio.pause();
            return;
        }
        const clipDuration = isPreviewSource && Number.isFinite(audio.duration) && audio.duration > 0
            ? audio.duration : Number(preview.totalTimeSeconds);
        // Restoring currentTime at the end can clear native `ended`. Treat
        // that position as replay too, with a small media timestamp tolerance.
        const isAtEnd = Number.isFinite(clipDuration) && clipDuration > 0 && audio.currentTime >= clipDuration - 0.05;
        const shouldReplay = replayAvailable || finishedPreviewIds.current.has(preview.id) ||
            (isPreviewSource && (audio.ended || isAtEnd));
        if (!onboardingSession) {
            if (shouldReplay) replayPreview();
            else resumePreviewPlayback();
            return;
        }
        if (audio.src !== preview.mp3URL) audio.src = preview.mp3URL;
        else if (audio.error) audio.load();
        if (shouldReplay) {
            previewListening.current?.reset();
            // A late metadata event must not restore the old finished position.
            if (restoreSamplePosition.current) {
                audio.removeEventListener('loadedmetadata', restoreSamplePosition.current);
                restoreSamplePosition.current = null;
            }
            audio.currentTime = 0;
            setCurTime(0);
            finishedPreviewIds.current.delete(preview.id);
            updateOnboardingSession({ position: 0 }, { newId: onboardingSession?.newId, previewId: preview.id });
            setReplayRequest(request => request + 1);
            setScrollToCurrentMessage(true);
        }
        requestPreviewPlayback().catch(() => {
            if (onboardingSession) {
                trackOnboardingError('playback', { code: audio.error ? 'media_error' : 'play_rejected' });
            }
        });
    };

    const closePreviewButton = !onSignupRequired ? (
        <button ref={closeButton} type='button' className={`previewArtworkCloseButton${missingPreview ? ' previewMissingCloseButton' : ''}`}
            onClick={onClose} aria-label='Close previews'>
            <span><CloseRounded aria-hidden='true' /></span>
        </button>
    ) : null;

    const previewControls = missingPreview ? null : (
        <div ref={controlsSurfaceRef} className='previewControlsSurface'>
            {duration > 0 ?
                <div className='previewProgressBarContainer'>
                    <ProgressBarThin progress={(curTime || 0) / duration} height={2} />
                </div>
                :
                <></>
            }

            <div className={`previewActionBar${onSignupRequired ? ' previewActionBarOnboarding' : ' previewEpisodeActionBar'}${showInitialPlayAction ? ' previewActionBarOnboardingStart' : ''}`}>
                {showInitialPlayAction ? <PrimaryActionButton
                    ref={initialPlayButton}
                    className={`previewInitialPlayButton${showPlayIntro ? ' previewInitialPlayButtonIntro' : ''}`}
                    onPointerDown={() => setShowPlayIntro(false)}
                    onClick={() => {
                        setShowPlayIntro(false);
                        togglePreviewPlayback();
                    }}
                    onAnimationEnd={() => setShowPlayIntro(false)}
                >
                    <PlayArrowRounded aria-hidden='true' style={{ fontSize: '22px' }} />
                    Play highlight
                </PrimaryActionButton> : onSignupRequired ? <>
                    <button ref={closeButton} type='button' onClick={onClose}
                        className='previewDismissButton' aria-label='Back to your highlights'>
                        <ChevronLeftRounded aria-hidden='true' style={{ fontSize: '26px' }} />
                    </button>
                    <button type='button' className='previewDismissButton previewOnboardingNextButton' onClick={nextPreview}>
                        Next<ChevronRightRounded aria-hidden='true' />
                    </button>
                </> : <>
                    <div className='previewEpisodeActions'>
                        <PreviewFullEpisodeButton previewId={preview.id} onClick={continueListening}
                            disabled={!hasFullEpisode} audio={audio} mediaURL={preview.mp3URL}
                            fallbackDuration={preview.totalTimeSeconds} enabled={countdownEnabled} />
                    </div>
                    <div className='previewUtilityControls'>
                        <MoreWithPlaybackSpeedButton
                            onMoreClick={togglePreviewActions(true)}
                            className='previewPlaybackButton previewUtilityButton'
                        />
                        <PreviewBookmarkButton preview={preview} className='previewCenterActionButton previewUtilityButton' />
                        <button type='button' onClick={fullEpisodeClick}
                            className='previewCenterActionButton previewUtilityButton'
                            aria-label='More options' disabled={!localPreview && !track}>
                            <MoreHorizRounded aria-hidden='true' style={{ fontSize: '22px' }} />
                        </button>
                    </div>
                </>}
            </div>
        </div>
    );

    return (
        <div id={preview.id} ref={previewCardViewport} className={`hideScrollbar previewCardShell${missingPreview ? ' previewCardMissing' : ''}`}
            style={{ height: `${cardHeight}px` }}>
            <StatusToast open={copied} message='Copied to clipboard' onClose={() => setCopied(false)} />
            <StatusToast tone='error' open={Boolean(shareError)} message={shareError} onClose={() => setShareError('')} />
            <PreviewReportIssueDrawer open={showReport} onClose={() => setShowReport(false)}
                onTransitionExited={reportExited} preview={preview} contentType='preview' playerSeconds={audio.currentTime || 0} />
            <PreviewSigninDrawer open={Boolean(signinAction)} action={signinAction}
                onClose={closeSignin} onSignedIn={completeActionSignin} onTransitionExited={signinExited} />
            {usesSharedChrome && isChromeOwner && createPortal(closePreviewButton, closeButtonHost)}
            {usesSharedChrome && isChromeOwner && createPortal(previewControls, controlsHost)}
            <div className={`previewCardFrame${showInitialPlayAction ? ' previewCardFrameOnboardingStart' : ''}`}>
                {pullDismissEnabled && <div className='previewPullDismissIndicator' aria-hidden='true'
                    data-pulling={pullDismiss.distance > 0 ? 'true' : undefined}
                    data-ready={pullDismiss.ready ? 'true' : undefined}
                    style={{ opacity: Math.min(1, pullDismiss.distance / 40),
                        transform: `translate(-50%, ${Math.min(30, pullDismiss.distance * 0.3) - 36}px)` }}>
                    <span />
                </div>}
                {!usesSharedChrome && closePreviewButton}
                {showInitialPlayAction && <button
                    ref={closeButton}
                    type='button'
                    onClick={onClose}
                    className='previewDismissButton previewInitialDismissButton'
                    aria-label='Back to your highlights'
                >
                    <ChevronLeftRounded aria-hidden='true' style={{ fontSize: '26px' }} />
                </button>}
                <div ref={transcriptContainerRef} className='preview-slider hideScrollbar'
                    data-pull-dismiss-enabled={pullDismissEnabled ? 'true' : undefined}
                    style={{ position: 'relative', height: `${sliderHeight}px` }}>
                    <EntityInfoDrawer
                        open={openPersonInfo}
                        onClose={togglePersonInfo(false)}
                        speakerId={selectedSpeakerId.current} />
                    <PreviewQueueDrawer
                        open={showQueue}
                        onClose={closeQueue}
                        onTransitionExited={queueExited}
                        onSelectPreview={selectQueuedPreview}
                        title={queuePresentation.title}
                        earlier={queuePresentation.earlier}
                        current={queuePresentation.current}
                        upcomingGroups={queuePresentation.upcomingGroups}
                    />
                    <PreviewMoreOrLessDrawer
                        open={showMoreOrLess}
                        onClose={closeMoreOrLess}
                        onTransitionExited={moreOrLessExited}
                        preview={preview}
                        authenticated={authenticated}
                        speakerSubscribeIds={speakerSubscribeIds}
                        speakerSubscribeStatusById={speakerSubscribeStatusById}
                        podcastSubscribeIds={podcastSubscribeIds}
                        podcastSubscribeStatusById={podcastSubscribeStatusById}
                        setSpeakerSubscribeStatus={setSpeakerSubscribeStatus}
                        setPodcastSubscribeStatus={setPodcastSubscribeStatus}
                        topicPreferenceStatusById={topicPreferenceStatusById}
                        topicPreferencesLoaded={topicPreferencesLoaded}
                        topicPreferencesLoading={topicPreferencesLoading}
                        topicPreferencesError={topicPreferencesError}
                        preloadTopicPreferences={preloadTopicPreferences}
                        setTopicPreferenceStatus={setTopicPreferenceStatus}
                    />
                    <Drawer
                        PaperProps={{
                            sx: {
                                borderRadius: '18px 18px 0px 0px'
                            }
                        }}
                        anchor='bottom' open={showPreviewActions} onClose={togglePreviewActions(false)}>
                        <div className='horizontalCenter appDrawerSurface hasCloseFooter'>
                            <div style={{ padding: '10px 0px 20px' }}>
                                <PreviewActions
                                    preview={preview}
                                    onSpeedClick={onSpeedClick}
                                />
                                <CloseFooter onClick={togglePreviewActions(false)} />
                            </div>
                        </div>
                    </Drawer>

                    <SwipeableDrawer
                        PaperProps={{
                            sx: {
                                borderRadius: '18px 18px 0px 0px',
                                maxHeight: 'calc(var(--app-drawer-height, 100vh) * 0.9)',
                                overflowY: 'auto',
                            }
                        }}
                        ModalProps={{ onTransitionExited: fullEpisodeDrawerExited }}
                        anchor='bottom' open={showFullEpisodeContinueOrStart} onClose={closeFullEpisodeDrawer}>
                        {!fullEpisodeInfo ?
                            <div className='horizontalCenter appDrawerSurface' style={{ padding: '20px' }}>
                                Unexpected Error.
                            </div>
                            :
                            <div style={{ padding: '20px 0px' }}>
                                <div className='horizontalCenter appDrawerSurface' style={{ padding: '0px 20px 30px' }}>
                                    <div className='previewPreferenceDragIndicator' />
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                        <div>
                                            <div className='heading' style={{ paddingTop: '16px', paddingBottom: '12px' }}>
                                                Summary
                                            </div>
                                            <PreviewInfoSummary
                                                summary={fullEpisodeSummary}
                                                topics={fullEpisodeTopics}
                                                style={{ fontSize: '16px', padding: '0px' }}
                                            />
                                        </div>

                                        <div>
                                            <div className='heading' style={{ paddingBottom: '12px' }}>
                                                Episode
                                            </div>
                                            <div style={{ fontSize: '14px', paddingBottom: '4px', color: 'var(--app-text-secondary)' }}>
                                                {getDateString(fullEpisodeInfo.publishedDate)}
                                            </div>
                                            <div style={{ fontSize: '16px', fontWeight: '500' }}>
                                                {fullEpisodeInfo.source.title}
                                            </div>
                                        </div>

                                        <div>
                                            <div className='heading' style={{ paddingBottom: '12px' }}>Show</div>
                                            <ShowRow source={fullEpisodeInfo.source} onOpen={goToPodcast} />
                                        </div>

                                        {fullEpisodeSpeakers.length > 0 && <div>
                                            <div className='heading' style={{ paddingBottom: '12px' }}>Speakers</div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                                {fullEpisodeSpeakers.map(speaker => <SpeakerRow key={speaker.id} speaker={speaker}
                                                    profileBackLabel='More Info' />)}
                                            </div>
                                        </div>}

                                        {localPreview?.trackMp3URL && <div>
                                            <FullWidthButton
                                                Icon={SkipPrevious}
                                                title='Start from beginning'
                                                onClick={startFromBeginning}
                                                showChevron={false}
                                                containerPadding='6px 0px'
                                                contentGap='10px'
                                                iconStyle={{ fontSize: '18px', verticalAlign: 'middle' }}
                                            />
                                            <ContinueListeningButton continueListening={continueListening}
                                                totalSeconds={fullEpisodeDuration} startSeconds={continuationSeconds} horizontalPadding='0px' />
                                        </div>}
                                        <div className='previewInfoOptionsRows'>
                                            <PreviewOptionRow Icon={IosShareRounded} title='Share' onClick={async () => {
                                                const url = `https://${LABEL_DOMAIN}/preview/${encodeURIComponent(preview.fullTrackId)}/${encodeURIComponent(preview.messageIndex)}`;
                                                setShareError('');
                                                setCopied(false);
                                                try {
                                                    if (await shareContent({ url }) === 'copied') setCopied(true);
                                                } catch (_) { setShareError(SHARE_FAILURE_MESSAGE); }
                                            }} />
                                            <PreviewOptionRow Icon={ReportProblemOutlined} title='Report'
                                                onClick={() => actionFromSummary('report')} />
                                            <PreviewOptionRow Icon={TuneOutlined} title={PREVIEW_PREFERENCE_COPY_TITLE}
                                                onClick={() => actionFromSummary('preferences')} />
                                            <PreviewOptionRow Icon={HomeOutlined} title='Home' onClick={() => { audio.pause(); navigate('/'); }} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        }
                    </SwipeableDrawer >

                    <div style={{ position: 'sticky', top: 0, marginTop: '-1px', maxHeight: '1px', textAlign: 'center' }}>
                        <span style={{ display: 'inline-block', minWidth: '1px' }} ref={currentViewport}></span>
                    </div>

                    {missingPreview ? <div className='previewMissingContext'>
                        <div className='previewMissingLabel'>Highlights</div>
                        <div className='previewMissingSource'>
                            {preview.source.imageURL && <img src={preview.source.imageURL} alt='' />}
                            <div>
                                <div className='previewMissingSourceName'>{preview.source.name}</div>
                                <div className='previewMissingTitle'>{preview.title}</div>
                            </div>
                        </div>
                    </div> : <div className='previewTopInfo'>
                        <PreviewImage
                            preview={preview}
                            isPreparingAudio={audioPreparationActive && playbackControl.preparing}
                            onPlaybackClick={showInitialPlayAction ? undefined : togglePreviewPlayback}
                            showPlaybackControl={playbackControl.showControl}
                            fadePlaybackControl={playbackControl.fadeControl}
                            playbackControlAvailable={playbackControl.available}
                            playing={playbackControl.playing}
                            playbackButtonRef={playbackButton}
                            topicLabel=''
                            onImageClick={fullEpisodeClick}
                            imageActionLabel={onSignupRequired ? 'Sign in to explore the full episode' : 'Open episode details'}
                            onSourceClick={navToSource(preview.source.id)}
                            imageBorderRadius='0px'
                        />

                        <div className='previewCardMeta'>
                            <button type='button' onClick={fullEpisodeClick} className='previewCardTitleButton'
                                aria-label={onSignupRequired ? `Sign in to explore the full episode: ${preview.title}` : undefined}>
                                <div className='previewCardTitle'>
                                    {preview.title}
                                </div>
                            </button>

                            {!onSignupRequired && publishedDateText && <time
                                className='previewCardPublishedDate'
                                dateTime={preview.publishedDate}
                                aria-label={`Published ${publishedDateText}`}
                            >
                                {publishedDateText}
                            </time>}

                            {!onSignupRequired && <PreviewTopicRow
                                presentation={queuePresentation}
                                topics={visibleRelatedTopics.filter(topic => previewTopicKey(topic.id) !== activeTopicKey)}
                                previewId={preview.id}
                                isActive={isCurrentViewport}
                                queueTriggerRef={queueTriggerRef}
                                onOpenQueue={openQueue}
                                onTopicClick={onTopicPreviewsClick}
                            />}

                        </div>
                        <div className='thinRightDivider previewTopDivider' />
                    </div>}

                    {/* 16px message + 20px transcript + 40px body = button clearance and breathing room. */}
                    <div className={`previewCardBody${missingPreview ? ' previewMissingBody' : ''}`}
                        style={missingPreview ? undefined : { paddingBottom: '40px' }}>
                        {missingPreview ?
                            <div className='previewMissingMessage'>
                                <h1>Highlight unavailable</h1>
                                <PrimaryActionButton className='previewMissingAction'
                                    onClick={nextQueuedPreview ? nextPreview : onClose}>
                                    {nextQueuedPreview ? 'Next' : 'Back to browsing'}
                                    <ChevronRightRounded aria-hidden='true' />
                                </PrimaryActionButton>
                            </div>
                            :
                            <div>

                                {!localPreview || reloadTranscript || showAudioPlayerDrawer ?
                                    <div className='previewCardLoadingShell'>
                                        <div className='previewCardSpeakerShell'>
                                            <SpeakerRow speaker={preview.person} variant='metadata' onSpeakerClick={onSignupRequired ? () => onSignupRequired('speaker') : undefined} />
                                        </div>
                                        <div
                                            className={`previewTranscriptLoading ${isCurrentViewport ? '' : 'previewTranscriptLoadingPaused'}`}
                                            role='status'
                                            aria-label='Loading transcript'
                                        >
                                            {PREVIEW_TRANSCRIPT_LOADING_LINE_WIDTHS.map((lineWidths, paragraphIndex) => (
                                                <div className='previewTranscriptLoadingParagraph' key={paragraphIndex}>
                                                    {lineWidths.map((lineWidth, lineIndex) => (
                                                        <div
                                                            className='loading previewTranscriptLoadingLine'
                                                            key={lineIndex}
                                                            style={{ width: lineWidth }}
                                                        />
                                                    ))}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    :
                                    <div>
                                        <Transcript
                                            pathname={localPreview.pathname}
                                            autoscrollPaddingBottom={40}
                                            curTime={isCurrentViewport ? curTime : 0}
                                            currentSection={0}
                                            previewSpeakerId={preview.person.id}
                                            isCurrentViewport={isCurrentViewport}
                                            openSpeakerInfo={openSpeakerInfo}
                                            onSignupRequired={onSignupRequired}
                                            onManualScroll={onTranscriptManualScroll}
                                            playing={playing}
                                            replayRequest={replayRequest}
                                            scrollToCurrentMessage={scrollToCurrentMessage}
                                            setScrollToCurrentMessage={setScrollToCurrentMessage}
                                            setCurrentSpeaker={(currentSpeaker) => {
                                                // unused current speacker
                                            }}
                                            transcriptContainerRef={transcriptContainerRef}
                                            topContent={<></>}
                                            track={localPreview}
                                        />
                                    </div>
                                }
                            </div>
                        }
                    </div>


                    {scrollToCurrentMessage || missingPreview ?
                        <></>
                        :
                        <SyncTranscriptAudio
                            bottom='16px'
                            scrollToCurrentMessage={scrollToCurrentMessage}
                            onSyncClick={onSyncClick}
                            track={localPreview}
                        />
                    }

                    {isCurrentViewport ? <></> :
                        <button type='button' onClick={scrollToPreviewCard} aria-label={`Go to preview: ${preview.title}`}>
                            <div className='notInViewportOverlay previewCardInactiveOverlay'>
                            </div>
                        </button>
                    }
                </div>

                {!usesSharedChrome && previewControls}

            </div>
        </div >
    );
};

const mapStateToProps = (state) => ({
    authenticated: state.user.authenticated,
    saveDrawerOpen: Boolean(state.savedHighlights?.selection),
    savePresentation: state.savedHighlights?.selection,
    nextPreviewsInfo: state.data.nextPreviewsInfo,
    track: state.data.track,
    speakerSubscribeIds: state.user.speakerSubscribeIds,
    speakerSubscribeStatusById: state.user.speakerSubscribeStatusById,
    podcastSubscribeIds: state.user.podcastSubscribeIds,
    podcastSubscribeStatusById: state.user.podcastSubscribeStatusById,
    topicPreferenceStatusById: state.user.topicPreferenceStatusById,
    topicPreferencesLoaded: state.user.topicPreferencesLoaded,
    topicPreferencesLoading: state.user.topicPreferencesLoading,
    topicPreferencesError: state.user.topicPreferencesError,
})

const mapActionsToProps = {
    appendRelatedPreviews,
    setNextPreviewsInfo,
    setTrack,
    setSpeakerSubscribeStatus,
    setPodcastSubscribeStatus,
    preloadTopicPreferences,
    setTopicPreferenceStatus,
}

PreviewCard.propTypes = {

}

export default connect(mapStateToProps, mapActionsToProps)(PreviewCard);
